import {
    IconClientUpdates,
    IconListChecks,
    IconPlans,
    IconSpecifications,
} from "@btnet/blueprint-icons";
import { BTLoginTypes } from "@btnet/services-btnet/handlers/models/bTLoginTypes";
import type { LegacyEntityActionExtraData } from "@btnet/services-btnet/handlers/models/legacyEntityActionExtraData";
import { NotificationTypes } from "@btnet/services-btnet/handlers/models/notificationTypes";

import { type BuilderInfo } from "helpers/AppProvider.types";

import {
    MediaPropertiesTab,
    MediaType,
    NotificationActionType,
    OpenActivityType,
} from "types/enum";

import { addQsParam } from "utilities/apiHandler";
import { reportError } from "utilities/errorHelpers";
import { dailyLogViewRoute } from "utilities/flatRoutes/dailyLog.routes";
import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { leadDetailRoute } from "utilities/flatRoutes/lead.routes";
import { type IRouteLocationState } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation.types";
import { isInPortal } from "utilities/portal/portal";
import { routes } from "utilities/routes";

import {
    BTIconBids,
    BTIconBillsPurchaseOrders,
    BTIconChangeOrders,
    BTIconContacts,
    BTIconDailyLogs,
    BTIconDocuments,
    BTIconEstimate,
    BTIconGearSix,
    BTIconJobs,
    BTIconLeadOpportunities,
    BTIconLeadProposals,
    BTIconMessages,
    BTIconOwnerInvoices,
    BTIconPhoneOutline,
    BTIconPhotos,
    BTIconPurchaseOrderPayments,
    BTIconPurchaseOrders,
    BTIconRebates,
    BTIconReceipts,
    BTIconRFIs,
    BTIconSchedule,
    BTIconSelections,
    BTIconSubsVendors,
    BTIconSurveys,
    BTIconTimeClock,
    BTIconTodos,
    BTIconToolOutlined,
    BTIconUmbrella,
    BTIconUsers,
    BTIconVideos,
    BTIconWarranty,
} from "commonComponents/btWrappers/BTIcon";
import { JobIdTypes } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { TemplateType } from "entity/document/Document/Document.api.types";
import { InternalUserTabKeys } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { JobDetailTab } from "entity/job/Job.api.types";
import { IntegrationEnums } from "entity/marketplace/MarketplaceDirectory/MarketplaceDirectory.api.types";
import { PurchasingDashboardPages } from "entity/rebate/PurchasingDashboard/PurchasingDashboard.types";
import { ScheduleListContainerTabs } from "entity/schedule/ScheduleListContainer/ScheduleListContainer.api.types";
import { SubSetupTabKeys } from "entity/sub/SubSetup/SubSetup.api.types";
import { SubTabKeys } from "entity/sub/SubVendor/SubVendor.api.types";

export function getPreferencesLink(userId: number, jobId?: number) {
    if (isInPortal({ builder: true })) {
        return routes.internalUsers.getInternalUserDetailsLink(
            userId,
            InternalUserTabKeys.Notifications,
        );
    } else if (isInPortal({ subs: true })) {
        return routes.sub.getSetupLink(userId, SubSetupTabKeys.Notifications);
    } else if (isInPortal({ owner: true })) {
        if (!jobId) {
            throw new Error("jobId is required for owner portal preferences link");
        }
        return routes.owner.getSetupLink(jobId);
    }
    return "";
}

export const getNotificationTypeTestId = (
    notificationTypeValue: NotificationTypes,
): keyof NotificationTypes | "unknown" => {
    const notificationType = Object.keys(NotificationTypes).find(
        (key) => NotificationTypes[key as keyof typeof NotificationTypes] === notificationTypeValue,
    ) as keyof NotificationTypes | undefined;
    if (!notificationType) {
        return "unknown";
    }
    return notificationType;
};

export interface INotificationInfo {
    name: string;
    icon: React.ReactNode;
    link?: string | IRouteLocationState;
    isFullPageRedirect?: boolean;
    isExternalLink?: boolean;
}

export function getNotificationInfo(
    actionType: NotificationActionType,
    entityId: number | null,
    jobId: number | null,
    recipientLoginType: BTLoginTypes,
    extraData: LegacyEntityActionExtraData,
    notificationType: NotificationTypes,
    builderInfo: BuilderInfo,
    notificationEntityId: number | null,
    externalId: string,
): INotificationInfo {
    const iconSize = 20;

    switch (actionType) {
        case NotificationActionType.Bid_Details:
        case NotificationActionType.BidPackage_Details:
            if (!entityId || !jobId) {
                reportError("BidPackage_Details: Missing entityId or jobId");
            }
            return {
                name: "Bid",
                icon: <BTIconBids size={iconSize} />,
                link: entityId && jobId ? routes.bid.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.Bill_Details:
            return {
                name: "Bill",
                icon: <BTIconBillsPurchaseOrders size={iconSize} />,
                link: generateBillsDetailsLink(entityId, jobId),
            };
        case NotificationActionType.Bill_ApprovalQueue:
            return {
                name: "Bill",
                icon: <BTIconBillsPurchaseOrders size={iconSize} />,
                link: routes.bill.getApprovalQueueLink(),
                isFullPageRedirect: true,
            };
        case NotificationActionType.Bill_List:
            return {
                name: "Bill",
                icon: <BTIconBillsPurchaseOrders size={iconSize} />,
                link: routes.bill.getListLink(),
                isFullPageRedirect: true,
            };
        case NotificationActionType.ChangeOrder_Details:
            if (!entityId || !jobId) {
                reportError("ChangeOrder_Details: Missing entityId or jobId");
            }
            return {
                name: "Change Order",
                icon: <BTIconChangeOrders size={iconSize} />,
                link: generateChangeOrderDetailsLink(entityId, jobId),
                isFullPageRedirect: true,
            };
        case NotificationActionType.CreditMemo_Details:
            if (!entityId || !jobId) {
                reportError("CreditMemo_Details: Missing entityId or jobId");
            }
            return {
                name: "Credit Memo",
                icon: <BTIconOwnerInvoices size={iconSize} />,
                link: entityId && jobId ? routes.creditMemos.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.DailyLog_Details:
            if (!entityId || !jobId) {
                reportError("DailyLog_Details: Missing entityId or jobId");
            }
            const openComments =
                notificationType === NotificationTypes.Daily_Log_Discussion_Added ||
                notificationType === NotificationTypes.UserMention;

            const discussionId = notificationEntityId ?? 0;
            const commentIdParam = openComments ? { commentId: discussionId } : undefined;
            return {
                name: "Daily Log",
                icon: <BTIconDailyLogs size={iconSize} />,
                link:
                    entityId && jobId
                        ? dailyLogViewRoute.link(
                              {
                                  id: entityId,
                                  jobId: jobId,
                              },
                              commentIdParam,
                          )
                        : "",
                isFullPageRedirect: true,
            };
        case NotificationActionType.Document_Details:
            if (!entityId) {
                reportError("Document_Details: Missing entityId");
            }
            return {
                name: "Document",
                icon: <BTIconDocuments size={iconSize} />,
                link: entityId
                    ? routes.media.getMediaPropertiesLink(
                          entityId,
                          MediaType.Document,
                          jobId ?? JobIdTypes.GlobalDocs,
                          extraData.folderId ?? undefined,
                      )
                    : "",
            };
        case NotificationActionType.JobProposal_Details:
            if (!entityId || !jobId) {
                reportError("JobProposal_Details: Missing entityId or jobId");
            }
            return {
                name: "Proposal",
                icon: <BTIconEstimate size={iconSize} />,
                link:
                    entityId && jobId
                        ? routes.jobPicker.selectJobLink(
                              jobId,
                              routes.proposal.getProposalDashboardLink(),
                          )
                        : "",
                isFullPageRedirect: true,
            };
        case NotificationActionType.InternalUser_Details:
            if (!entityId) {
                reportError("InternalUser_Details: Missing entityId");
            }
            return {
                name: "User",
                icon: <BTIconUsers size={iconSize} />,
                link: entityId ? routes.internalUsers.getInternalUserDetailsLink(entityId) : "",
            };
        case NotificationActionType.InternalUser_Details_NotificationSettings:
            if (!entityId) {
                reportError("InternalUser_Details_NotificationSettings: Missing entityId");
            }
            return {
                name: "User",
                icon: <BTIconUsers size={iconSize} />,
                link: entityId
                    ? routes.internalUsers.getInternalUserDetailsLink(
                          entityId,
                          InternalUserTabKeys.Notifications,
                      )
                    : "",
            };
        case NotificationActionType.Jobsite_Details:
            if (!entityId) {
                reportError("Jobsite_Details: Missing entityId");
            }
            let jobsiteLink: string | IRouteLocationState = "";
            if (entityId) {
                jobsiteLink = jobDetailRoute.link({ jobId: entityId });
            }
            return {
                name: "Job",
                icon: <BTIconJobs size={iconSize} />,
                link: jobsiteLink,
                isFullPageRedirect: true,
            };
        case NotificationActionType.Jobsite_Details_BuildersRiskInsurance:
            if (!entityId) {
                reportError("Jobsite_Details_BuildersRiskInsurance: Missing entityId");
            }
            let briLink: string | IRouteLocationState = "";
            if (entityId) {
                briLink = jobDetailRoute.link({
                    jobId: entityId,
                    defaultTab: JobDetailTab.BRI,
                });
            }
            return {
                name: "Builder's Risk Insurance",
                icon: <BTIconUmbrella size={iconSize} />,
                link: briLink,
                isFullPageRedirect: true,
            };
        case NotificationActionType.Jobsite_Details_Owner:
            if (!entityId) {
                reportError("Jobsite_Details_Owner: Missing entityId");
            }
            let ownerLink: string | IRouteLocationState = "";
            if (entityId) {
                ownerLink = jobDetailRoute.link({
                    jobId: entityId,
                    defaultTab: JobDetailTab.Owner,
                });
            }
            return {
                name: "Client",
                icon: <BTIconContacts size={iconSize} />,
                link: ownerLink,
                isFullPageRedirect: true,
            };
        case NotificationActionType.LeadActivity_Details:
            if (!entityId || !extraData?.relatedId) {
                reportError("LeadActivity_Details: Missing entityId or extraData.relatedId");
            }
            return {
                name: "Lead Activity",
                icon: <BTIconPhoneOutline size={iconSize} />,
                link:
                    entityId && extraData?.relatedId
                        ? routes.leadActivity.getDetailsLink(
                              entityId,
                              extraData.relatedId,
                              OpenActivityType.ViewActivity,
                              undefined,
                          )
                        : "",
            };
        case NotificationActionType.Lead_Details:
            if (!entityId) {
                reportError("Lead_Details: Missing entityId");
            }
            // The page and the modal live at different paths (/LeadPage/:id vs /Lead/:id), so both
            // `link` and `isFullPageRedirect` switch on the flag. Warranty only flips the redirect
            // because its path is shared.
            let leadLink: string | IRouteLocationState = "";
            if (entityId) {
                leadLink = builderInfo.flags.leadsFullPageConversion
                    ? leadDetailRoute.link({ leadId: entityId })
                    : routes.lead.getDetailsLink(entityId);
            }
            return {
                name: "Lead",
                icon: <BTIconLeadOpportunities size={iconSize} />,
                link: leadLink,
                isFullPageRedirect: builderInfo.flags.leadsFullPageConversion,
            };
        case NotificationActionType.LeadProposal_Details:
            if (!entityId || !extraData?.relatedId) {
                reportError("LeadProposal_Details: Missing entityId or extraData.relatedId");
            }

            const isClient =
                recipientLoginType === BTLoginTypes.OWNER ||
                recipientLoginType === BTLoginTypes.CONTACT;

            const isValidClientLink = isClient && !!extraData.shareToken;
            if (isClient && !isValidClientLink) {
                reportError("LeadProposal_Details: Client portal missing share token");
                return {
                    name: "",
                    icon: null,
                    link: "",
                };
            }
            return {
                name: "Lead Proposal",
                icon: <BTIconLeadProposals size={iconSize} />,
                link:
                    entityId && extraData?.relatedId
                        ? isValidClientLink && extraData.shareToken
                            ? routes.share.getLink(extraData.shareToken)
                            : routes.leadProposals.getDetailsLink(
                                  entityId,
                                  extraData.relatedId,
                                  false,
                              )
                        : "",
                isFullPageRedirect: !!entityId && !!extraData?.relatedId && isValidClientLink,
                isExternalLink: isValidClientLink,
            };
        case NotificationActionType.LienWaiver_Details:
            if (!entityId || !jobId) {
                reportError("LienWaiver_Details: Missing entityId or jobId");
            }
            return {
                name: "Lien Waiver",
                icon: <BTIconBillsPurchaseOrders size={iconSize} />,
                link: entityId && jobId ? routes.lienWaiver.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.Message_Details:
            return {
                name: "Message",
                icon: <BTIconMessages size={iconSize} />,
                link: generateMessageDetailsLink(entityId, extraData?.folderId),
            };
        case NotificationActionType.Owner_Setup:
            if (!jobId) {
                reportError("Owner_Setup: Missing jobId");
            }
            return {
                name: "Client Setup",
                icon: <BTIconGearSix size={iconSize} />,
                link: jobId ? routes.owner.getSetupLink(jobId) : "",
            };
        case NotificationActionType.OwnerPayment_Details:
            if (!entityId || !jobId) {
                reportError("OwnerPayment_Details: Missing entityId or jobId");
            }
            return {
                name: isInPortal({ owner: true }) ? "Invoice" : "Client Invoice",
                icon: <BTIconOwnerInvoices size={iconSize} />,
                link: entityId && jobId ? routes.ownerInvoice.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.Payment_Details:
            if (!entityId || !jobId) {
                reportError("Payment_Details: Missing entityId or jobId");
            }
            return {
                name: "Purchase Order Payment",
                icon: <BTIconPurchaseOrderPayments size={iconSize} />,
                link:
                    entityId && jobId
                        ? routes.purchaseOrderPayment.getDetailsLink(entityId, jobId)
                        : "",
            };
        case NotificationActionType.Photo_Details:
            if (!entityId) {
                reportError("Photo_Details: Missing entityId");
            }
            return {
                name: "Photo",
                icon: <BTIconPhotos size={iconSize} />,
                link: entityId
                    ? routes.media.getMediaPropertiesLink(
                          entityId,
                          MediaType.Photo,
                          jobId ?? JobIdTypes.GlobalDocs,
                          extraData.folderId ?? undefined,
                      )
                    : "",
            };
        case NotificationActionType.PurchaseOrder_Details:
            if (!entityId || !jobId) {
                reportError("PurchaseOrder_Details: Missing entityId or jobId");
            }
            return {
                name: "Purchase Order",
                icon: <BTIconPurchaseOrders size={iconSize} />,
                link: entityId && jobId ? routes.purchaseOrder.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.Receipt_Details:
            if (!entityId) {
                reportError("Receipt_Details: Missing entityId");
            }
            return {
                name: "Receipt",
                icon: <BTIconReceipts size={iconSize} />,
                link: entityId ? routes.receipts.getDetailsLink(entityId) : "",
            };
        case NotificationActionType.RFI_Details:
            if (!entityId || !jobId) {
                reportError("RFI_Details: Missing entityId or jobId");
            }
            return {
                name: "RFI",
                icon: <BTIconRFIs size={iconSize} />,
                link: entityId && jobId ? routes.rfi.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.Schedule_Details:
            if (!entityId || !jobId) {
                reportError("Schedule_Details: Missing entityId or jobId");
            }
            return {
                name: "Schedule",
                icon: <BTIconSchedule size={iconSize} />,
                link: entityId && jobId ? routes.schedule.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.SelectionChoice_Details:
            return {
                name: "Selection Choice",
                icon: <BTIconSelections size={iconSize} />,
                link: generateSelectionChoiceLink(entityId, jobId, extraData?.relatedId),
            };
        case NotificationActionType.Selection_Details:
            if (!entityId || !jobId) {
                reportError("Selection_Details: Missing entityId or jobId");
            }
            return {
                name: "Selection",
                icon: <BTIconSelections size={iconSize} />,
                link: entityId && jobId ? routes.selection.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.SelectionOption_Details:
            if (!entityId || !jobId) {
                reportError("SelectionOption_Details: Missing entityId or jobId");
            }
            return {
                name: "Selection Option",
                icon: <BTIconSelections size={iconSize} />,
                link:
                    entityId && jobId
                        ? routes.selectionOptions.getDetailsLink(entityId, jobId)
                        : "",
                // The option detail is a full page; navigate directly instead of nesting under /NotificationPanel.
                isFullPageRedirect: true,
            };
        case NotificationActionType.PdfSignatureRequest_Details:
            if (!entityId || !jobId || !extraData?.relatedId || !extraData?.folderId) {
                reportError(
                    "PdfSignatureRequest_Details: Missing entityId, jobId, extraData.relatedId, or extraData.folderId",
                );
            }
            return {
                name: "Signature Request",
                icon: <BTIconDocuments size={iconSize} />,
                link:
                    entityId && jobId && extraData?.relatedId && extraData?.folderId
                        ? generateSignatureRequestLink(
                              jobId,
                              extraData?.relatedId,
                              extraData?.folderId,
                              MediaPropertiesTab.Signatures,
                          )
                        : "",
            };
        case NotificationActionType.PdfSignature_Details_Signing:
            if (!entityId || !jobId || !extraData?.relatedId || !extraData?.folderId) {
                reportError(
                    "PdfSignature_Details_Signing: Missing entityId, jobId, extraData.relatedId, or extraData.folderId",
                );
            }
            return {
                name: "Signature Request",
                icon: <BTIconDocuments size={iconSize} />,
                link:
                    entityId && jobId && extraData?.relatedId && extraData?.folderId
                        ? generateSignatureRequestLink(
                              jobId,
                              extraData?.relatedId,
                              extraData?.folderId,
                          )
                        : "",
            };
        case NotificationActionType.Sub_Details:
            if (!entityId) {
                reportError("Sub_Details: Missing entityId");
            }
            return {
                name: "Sub/Vendor",
                icon: <BTIconSubsVendors size={iconSize} />,
                link: entityId ? routes.sub.getDetailsLink(entityId) : "",
            };
        case NotificationActionType.Sub_Details_NotificationSettings:
            if (!entityId) {
                reportError("Sub_Details_NotificationSettings: Missing entityId");
            }
            return {
                name: "Sub/Vendor",
                icon: <BTIconSubsVendors size={iconSize} />,
                link: entityId ? routes.sub.getDetailsLink(entityId, SubTabKeys.Notifications) : "",
            };
        case NotificationActionType.Sub_Details_TradeAgreement:
            if (!entityId) {
                reportError("Sub_Details_TradeAgreement: Missing entityId");
            }
            return {
                name: "Sub/Vendor",
                icon: <BTIconSubsVendors size={iconSize} />,
                link: entityId
                    ? routes.sub.getDetailsLink(entityId, SubTabKeys.TradeAgreement)
                    : "",
            };
        case NotificationActionType.SubSetup_Details:
            if (!entityId) {
                reportError("SubSetup_Details: Missing entityId");
            }
            return {
                name: "Sub Setup",
                icon: <BTIconGearSix size={iconSize} />,
                link: entityId ? routes.sub.getSetupLink(entityId) : "",
            };
        case NotificationActionType.SubSetup_Details_Certificates:
            if (!entityId) {
                reportError("SubSetup_Details_Certificates: Missing entityId");
            }
            return {
                name: "Sub Setup",
                icon: <BTIconGearSix size={iconSize} />,
                link: entityId
                    ? routes.sub.getSetupLink(entityId, SubSetupTabKeys.CertificatesContracts)
                    : "",
            };
        case NotificationActionType.SubSetup_Details_NotificationSettings:
            if (!entityId) {
                reportError("SubSetup_Details_NotificationSettings: Missing entityId");
            }
            return {
                name: "Sub Setup",
                icon: <BTIconGearSix size={iconSize} />,
                link: entityId
                    ? routes.sub.getSetupLink(entityId, SubSetupTabKeys.Notifications)
                    : "",
            };
        case NotificationActionType.SubSetup_Details_TradeAgreement:
            if (!entityId) {
                reportError("SubSetup_Details_TradeAgreement: Missing entityId");
            }
            return {
                name: "Sub Setup",
                icon: <BTIconGearSix size={iconSize} />,
                link: entityId
                    ? routes.sub.getSetupLink(entityId, SubSetupTabKeys.TradeAgreement)
                    : "",
            };
        case NotificationActionType.Survey_Details:
            if (!entityId || !jobId) {
                reportError("Survey_Details: Missing entityId or jobId");
            }
            return {
                name: "Survey",
                icon: <BTIconSurveys size={iconSize} />,
                link: entityId && jobId ? routes.survey.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.SurveyOwner_Details:
            if (!entityId) {
                reportError("SurveyOwner_Details: Missing entityId");
            }
            return {
                name: "Survey",
                icon: <BTIconSurveys size={iconSize} />,
                link: entityId ? routes.survey.getOwnerSurvey(entityId) : "",
            };
        case NotificationActionType.TimeClock_ClockOut:
            if (!entityId) {
                reportError("TimeClock_ClockOut: Missing entityId");
            }
            return {
                name: "Time Clock",
                icon: <BTIconTimeClock size={iconSize} />,
                link: entityId
                    ? routes.timeClock.getClockInClockOutLink(
                          entityId,
                          jobId ?? JobIdTypes.GlobalJob,
                          false,
                          false,
                      )
                    : "",
            };
        case NotificationActionType.TimeClock_Details:
            if (!entityId) {
                reportError("TimeClock_Details: Missing entityId");
            }
            let link = "";
            if (entityId) {
                link = routes.timeClock.getShiftDetailsLink(
                    entityId,
                    jobId ?? JobIdTypes.GlobalJob,
                );
            }
            return {
                name: "Time Clock",
                icon: <BTIconTimeClock size={iconSize} />,
                link,
            };
        case NotificationActionType.ToDo_Details:
            if (!entityId || !jobId) {
                reportError("ToDo_Details: Missing entityId or jobId");
            }
            return {
                name: "To-Do",
                icon: <BTIconTodos size={iconSize} />,
                link: entityId && jobId ? routes.toDo.getDetailsLink(entityId, jobId) : "",
            };
        case NotificationActionType.Videos_Details:
            if (!entityId) {
                reportError("Videos_Details: Missing entityId");
            }
            return {
                name: "Video",
                icon: <BTIconVideos size={iconSize} />,
                link: entityId
                    ? routes.media.getMediaPropertiesLink(
                          entityId,
                          MediaType.Video,
                          jobId ?? JobIdTypes.GlobalDocs,
                          extraData.folderId ?? undefined,
                      )
                    : "",
            };
        case NotificationActionType.Warranty_Details:
            if (!entityId || !jobId) {
                reportError("Warranty_Details: Missing entityId or jobId");
            }
            return {
                name: "Warranty",
                icon: <BTIconWarranty size={iconSize} />,
                link: entityId && jobId ? routes.warranty.getDetailsLink(entityId, jobId) : "",
                isFullPageRedirect: builderInfo.flags.warrantiesModalConversion,
            };
        case NotificationActionType.WarrantyService_Details:
            if (!entityId || !jobId || !extraData?.relatedId || extraData?.completed == null) {
                reportError(
                    "WarrantyService_Details: Missing entityId, jobId, extraData.relatedId, or extraData.completed",
                );
            }
            return {
                name: "Warranty Service",
                icon: <BTIconWarranty size={iconSize} />,
                link:
                    entityId && jobId && extraData?.relatedId && extraData?.completed != null
                        ? builderInfo.flags.warrantiesModalConversion
                            ? routes.warranty.getServicePageLink(
                                  extraData.relatedId,
                                  jobId,
                                  entityId,
                                  extraData.completed,
                              )
                            : routes.warranty.getServiceLink(
                                  extraData.relatedId,
                                  jobId,
                                  entityId,
                                  extraData.completed,
                              )
                        : "",
                isFullPageRedirect: builderInfo.flags.warrantiesModalConversion,
            };
        case NotificationActionType.Deposit_Details:
            if (!entityId || !jobId) {
                reportError("Deposit_Details: Missing entityId or jobId");
            }
            return {
                name: "Deposit",
                icon: <BTIconOwnerInvoices size={iconSize} />,
                link:
                    entityId && jobId ? routes.deposits.getDetailsLink(entityId, jobId, false) : "",
            };
        case NotificationActionType.Integration_Settings:
            return {
                name: "Integration",
                icon: <BTIconToolOutlined size={iconSize} />,
                link: "",
            };
        case NotificationActionType.HubSpot_Settings:
            return {
                name: "HubSpot",
                icon: <BTIconToolOutlined size={iconSize} />,
                link: routes.marketplace.getintegrationSettingsLink(IntegrationEnums.HubSpot),
            };
        case NotificationActionType.Salesforce_Settings:
            return {
                name: "Salesforce",
                icon: <BTIconToolOutlined size={iconSize} />,
                link: routes.marketplace.getintegrationSettingsLink(IntegrationEnums.Salesforce),
            };
        case NotificationActionType.Pipedrive_Settings:
            return {
                name: "Pipedrive",
                icon: <BTIconToolOutlined size={iconSize} />,
                link: routes.marketplace.getintegrationSettingsLink(IntegrationEnums.Pipedrive),
            };
        case NotificationActionType.Reconsent_Details:
            return {
                name: "Marketplace",
                icon: <BTIconToolOutlined size={iconSize} />,
                link: routes.marketplace.reconsent,
            };
        case NotificationActionType.Rebates_List:
            return {
                name: "Rebate",
                icon: <BTIconRebates size={iconSize} />,
                link: routes.purchasing.getDashboardLink(PurchasingDashboardPages.Activity),
                isFullPageRedirect: true,
            };
        case NotificationActionType.ClientUpdate_Details:
            if (!entityId || !jobId) {
                reportError("ClientUpdate_Details: Missing entityId or jobId");
            }
            return {
                name: isInPortal({ owner: true }) ? "Project Update" : "Client Update",
                icon: <IconClientUpdates />,
                link:
                    entityId && jobId
                        ? routes.jobPicker.selectJobLink(
                              jobId,
                              routes.clientUpdates.getFullDetailsLink(entityId, recipientLoginType),
                          )
                        : "",
                isFullPageRedirect: true,
            };
        case NotificationActionType.ClientUpdate_List:
            return {
                name: isInPortal({ owner: true }) ? "Project Update" : "Client Update",
                icon: <IconClientUpdates />,
                link: routes.clientUpdates.getListLink(),
                isFullPageRedirect: true,
            };
        case NotificationActionType.Task_Details:
            return {
                name: "Task",
                icon: <IconListChecks />,
                link: jobId
                    ? routes.jobPicker.selectJobLink(jobId, routes.task.getDetailsLink(externalId))
                    : "",
                isFullPageRedirect: true,
            };
        case NotificationActionType.Tasks_List:
            return {
                name: "Tasks",
                icon: <IconListChecks />,
                link: routes.jobPicker.selectJobLink(
                    JobIdTypes.AllJobs,
                    routes.task.getMyTasksLink(recipientLoginType),
                ),
                isFullPageRedirect: true,
            };
        case NotificationActionType.Specification_Details:
            return {
                name: "Specification",
                icon: <IconSpecifications />,
                link: jobId
                    ? routes.jobPicker.selectJobLink(
                          jobId,
                          routes.specification.getBookLink(externalId),
                      )
                    : "",
                isFullPageRedirect: true,
            };
        case NotificationActionType.PlanPage_Details: {
            if (!jobId) {
                return {
                    name: "Plan",
                    icon: <IconPlans />,
                    link: "",
                    isFullPageRedirect: true,
                };
            }
            let viewerLink = routes.plans.getViewerLink(externalId);
            if (notificationType === NotificationTypes.PlanPage_Discussion_Added) {
                viewerLink += addQsParam(`commentId=${notificationEntityId ?? 0}`, viewerLink);
            }
            return {
                name: "Plan",
                icon: <IconPlans />,
                link: routes.jobPicker.selectJobLink(jobId, viewerLink),
                isFullPageRedirect: true,
            };
        }
        case NotificationActionType.PlanSet_List: {
            // Plan-added / revision bell entries group by set and route to the plans list with the set
            // preselected (externalId = set external id, resolved server-side by the PlanSet hydrator).
            if (!jobId) {
                return {
                    name: "Plans",
                    icon: <IconPlans />,
                    link: "",
                    isFullPageRedirect: true,
                };
            }
            return {
                name: "Plans",
                icon: <IconPlans />,
                link: routes.plans.getPlansListLink(externalId, jobId),
                isFullPageRedirect: true,
            };
        }
        // Aggregate/multi-entity notifications carry no single entity id and are routed to the
        // relevant list instead of a detail page (server degrades *_Details -> *_List when the id
        // is null). Mirrors the Bill_List / Tasks_List pattern.
        case NotificationActionType.Schedule_List:
            return {
                name: "Schedule",
                icon: <BTIconSchedule size={iconSize} />,
                link: routes.schedule.getListLink(ScheduleListContainerTabs.Default),
                isFullPageRedirect: true,
            };
        case NotificationActionType.PurchaseOrder_List:
            return {
                name: "Purchase Order",
                icon: <BTIconPurchaseOrders size={iconSize} />,
                link: routes.purchaseOrder.getListLink(),
                isFullPageRedirect: true,
            };
        case NotificationActionType.OwnerPayment_List:
            return {
                name: isInPortal({ owner: true }) ? "Invoice" : "Client Invoice",
                icon: <BTIconOwnerInvoices size={iconSize} />,
                link: routes.ownerInvoice.getListLink(),
                isFullPageRedirect: true,
            };
        case NotificationActionType.Document_List:
            return {
                name: "Document",
                icon: <BTIconDocuments size={iconSize} />,
                link: routes.document.getListLink(),
                isFullPageRedirect: true,
            };
        case NotificationActionType.Photo_List:
            return {
                name: "Photo",
                icon: <BTIconPhotos size={iconSize} />,
                link: routes.photo.getListLink(),
                isFullPageRedirect: true,
            };
        case NotificationActionType.Video_Lists:
            return {
                name: "Video",
                icon: <BTIconVideos size={iconSize} />,
                link: routes.video.getListLink(),
                isFullPageRedirect: true,
            };
        default:
            return {
                name: "",
                icon: null,
                link: "",
            };
    }
}

function generateMessageDetailsLink(entityId?: number | null, folderId?: number | null): string {
    if (entityId && folderId) {
        return routes.messages.getMessageDetailsLink(entityId, folderId);
    } else if (entityId && (folderId === undefined || folderId == null)) {
        return routes.messages.getMessageDetailsLink(entityId);
    } else {
        reportError("Message_Details: Missing entityId or folderId");
        return "";
    }
}

function generateSelectionChoiceLink(
    entityId?: number | null,
    jobId?: number | null,
    relatedId?: number | null,
): string {
    if (entityId && jobId && relatedId) {
        return (
            routes.selection.getDetailsLink(relatedId, jobId) +
            routes.selectionChoice.getDetailsLink(entityId, jobId, relatedId)
        );
    } else if (entityId && jobId && (relatedId === undefined || relatedId == null)) {
        return routes.selectionChoice.getDetailsLink(entityId, jobId);
    } else {
        reportError("SelectionChoice_Details: Missing entityId, jobId, or relatedId");
        return "";
    }
}

function generateSignatureRequestLink(
    jobId: number,
    documentInstanceId: number,
    folderId: number,
    activeTab?: MediaPropertiesTab,
): string {
    return routes.media.getMediaPropertiesLink(
        documentInstanceId,
        MediaType.Document,
        jobId,
        folderId ?? 0,
        TemplateType.None,
        0,
        activeTab,
    );
}

function generateBillsDetailsLink(entityId?: number | null, jobId?: number | null): string {
    if (entityId && jobId) {
        return routes.bill.getDetailsLink(entityId, jobId);
    } else {
        reportError("Bill_Details: Missing entityId or jobId");
        return "";
    }
}

function generateChangeOrderDetailsLink(entityId: number | null, jobId: number | null): string {
    if (entityId && jobId) {
        return routes.changeOrder.getChangeOrderDetailsFullPage(entityId, jobId);
    } else {
        return "";
    }
}
